// Termine-Übersicht für die Praxis.
//
// Tab-Filter: Offene, Angenommene, Abgelehnte, Stornierte.
// Klick auf Patient öffnet Termin-Vorschlag-Modal.

import { useEffect, useState } from 'react';
import {
  cancelSlot,
  createSlotsBulk,
  deleteTerminVorschlag,
  listPatienten,
  listSlots,
  listTermine,
  vorschlagTermin,
  type PatientPublic,
  type Slot,
  type TerminVorschlag,
} from '../api';

type TopTab = 'vorschlaege' | 'slots';

export default function Termine() {
  const [topTab, setTopTab] = useState<TopTab>('vorschlaege');
  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Termine</h1>
          <p className="muted">
            <strong>Vorschläge</strong>: einzelnen Patient anfragen.{' '}
            <strong>Freie Slots</strong>: Termine veröffentlichen, die Patienten selbst buchen können.
          </p>
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 8 }}>
        <button className={`tab ${topTab === 'vorschlaege' ? 'active' : ''}`} onClick={() => setTopTab('vorschlaege')}>
          Vorschläge (1:1)
        </button>
        <button className={`tab ${topTab === 'slots' ? 'active' : ''}`} onClick={() => setTopTab('slots')}>
          Freie Slots
        </button>
      </div>

      {topTab === 'vorschlaege' ? <VorschlagSection /> : <SlotsSection />}
    </div>
  );
}

function VorschlagSection() {
  const [termine, setTermine] = useState<TerminVorschlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'offen' | 'beantwortet' | 'alle'>('offen');
  const [createOpen, setCreateOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await listTermine();
      setTermine(r.termine);
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const filtered = termine.filter((t) => {
    if (filter === 'alle') return true;
    if (filter === 'offen') return t.status === 'offen';
    if (filter === 'beantwortet') return t.status === 'angenommen' || t.status === 'abgelehnt';
    return true;
  });

  const onStorno = async (terminId: string) => {
    if (!confirm('Vorschlag zurückziehen? Der Eintrag verschwindet bei Ihnen und beim Patient (mit Push-Benachrichtigung).')) return;
    try {
      await deleteTerminVorschlag(terminId);
      reload();
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    }
  };

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <p className="muted" style={{ margin: 0 }}>
          Direkter Vorschlag an einen bestimmten Patienten. Push-Benachrichtigung + Annehmen/Ablehnen.
        </p>
        <button className="btn-primary" onClick={() => setCreateOpen(true)}>+ Termin vorschlagen</button>
      </div>

      <div className="toolbar">
        <div className="tabs">
          {(['offen', 'beantwortet', 'alle'] as const).map((s) => {
            const count = s === 'alle' ? termine.length
              : s === 'offen' ? termine.filter((t) => t.status === 'offen').length
              : termine.filter((t) => t.status === 'angenommen' || t.status === 'abgelehnt').length;
            return (
              <button key={s} className={`tab ${filter === s ? 'active' : ''}`} onClick={() => setFilter(s)}>
                {s === 'offen' ? 'Offen' : s === 'beantwortet' ? 'Beantwortet' : 'Alle'} ({count})
              </button>
            );
          })}
        </div>
        <button className="btn-secondary" onClick={reload}>↻ Neu laden</button>
      </div>

      {loading ? (
        <div className="empty-card">Lädt…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-card">
          <div className="empty-icon">📅</div>
          <h3>Keine Termine in dieser Kategorie</h3>
          {filter === 'offen' && <p>Schlagen Sie einem Patienten oben rechts einen Termin vor.</p>}
        </div>
      ) : (
        <div className="termine-grid">
          {filtered.map((t) => <TerminCard key={t.id} termin={t} onStorno={onStorno} />)}
        </div>
      )}

      {createOpen && (
        <CreateModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); reload(); }}
        />
      )}
    </div>
  );
}

// ─── Sektion: Freie Slots ───────────────────────────────────────────

function SlotsSection() {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [filter, setFilter] = useState<'frei' | 'gebucht' | 'alle'>('frei');

  const reload = async () => {
    setLoading(true);
    try {
      const r = await listSlots();
      setSlots(r.slots);
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const filtered = slots.filter((s) => {
    if (filter === 'alle') return true;
    return s.status === filter;
  });

  const onCancel = async (slotId: string) => {
    if (!confirm('Slot stornieren? Falls bereits gebucht, wird der Patient nicht automatisch informiert.')) return;
    try {
      await cancelSlot(slotId);
      reload();
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    }
  };

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <p className="muted" style={{ margin: 0 }}>
          Veröffentlichen Sie freie Termine. Jeder verknüpfte Patient sieht und bucht sie selbst.
        </p>
        <button className="btn-primary" onClick={() => setCreateOpen(true)}>+ Freie Slots anlegen</button>
      </div>

      <div className="toolbar">
        <div className="tabs">
          {(['frei', 'gebucht', 'alle'] as const).map((s) => {
            const count = s === 'alle' ? slots.length : slots.filter((x) => x.status === s).length;
            return (
              <button key={s} className={`tab ${filter === s ? 'active' : ''}`} onClick={() => setFilter(s)}>
                {s === 'frei' ? 'Frei' : s === 'gebucht' ? 'Gebucht' : 'Alle'} ({count})
              </button>
            );
          })}
        </div>
        <button className="btn-secondary" onClick={reload}>↻ Neu laden</button>
      </div>

      {loading ? (
        <div className="empty-card">Lädt…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-card">
          <div className="empty-icon">📅</div>
          <h3>Keine Slots in dieser Kategorie</h3>
          {filter === 'frei' && <p>Legen Sie oben rechts neue freie Slots an.</p>}
        </div>
      ) : (
        <div className="slots-grid">
          {filtered.map((s) => <SlotCard key={s.id} slot={s} onCancel={onCancel} />)}
        </div>
      )}

      {createOpen && (
        <SlotsBulkModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); reload(); }}
        />
      )}
    </div>
  );
}

function SlotCard({ slot, onCancel }: { slot: Slot; onCancel: (id: string) => void }) {
  const past = Date.parse(`${slot.termin_datum}T${slot.termin_uhrzeit}:00`) < Date.now();
  return (
    <div className={`slot-card status-${slot.status} ${past ? 'past' : ''}`}>
      <div className="slot-time">
        <div className="d-day">{Number(slot.termin_datum.split('-')[2])}</div>
        <div className="d-month">{monthShort(slot.termin_datum)}</div>
      </div>
      <div style={{ flex: 1 }}>
        <div className="slot-clock">{slot.termin_uhrzeit} <span className="muted xs">· {slot.dauer_minuten} Min.</span></div>
        <span className={`status status-${slot.status === 'frei' ? 'eingegangen' : slot.status === 'gebucht' ? 'bestaetigt' : 'erledigt'}`}>
          {slot.status === 'frei' ? 'Frei' : slot.status === 'gebucht' ? 'Gebucht' : 'Storniert'}
        </span>
        {slot.notiz && <div className="muted small" style={{ marginTop: 4 }}>{slot.notiz}</div>}
        {slot.patient_name && (
          <div style={{ marginTop: 6, fontSize: 13 }}>
            <strong>👤 {slot.patient_name}</strong>
            {slot.gebucht_am && (
              <span className="muted xs"> · gebucht {new Date(slot.gebucht_am).toLocaleString('de-DE')}</span>
            )}
          </div>
        )}
        {slot.patient_kommentar && (
          <div className="muted small" style={{ marginTop: 4 }}>💬 {slot.patient_kommentar}</div>
        )}
      </div>
      {slot.status !== 'storniert' && !past && (
        <button className="btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={() => onCancel(slot.id)}>
          Stornieren
        </button>
      )}
    </div>
  );
}

function SlotsBulkModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const tomorrow = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
  const [datum, setDatum] = useState(tomorrow);
  const [von, setVon] = useState('09:00');
  const [bis, setBis] = useState('12:00');
  const [dauer, setDauer] = useState(15);
  const [notiz, setNotiz] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Vorschau der Slots
  const preview = generateTimeSlots(von, bis, dauer);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (preview.length === 0) {
      alert('Keine Slots im Zeitraum.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await createSlotsBulk({
        termin_datum: datum,
        uhrzeiten: preview,
        dauer_minuten: dauer,
        notiz: notiz.trim() || undefined,
      });
      if (r.skipped.length > 0) {
        alert(`${r.created.length} Slots angelegt, ${r.skipped.length} übersprungen (Duplikate):\n\n${r.skipped.join('\n')}`);
      }
      onCreated();
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-card" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Freie Slots anlegen</h2>
          <button className="btn-icon" onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit} className="modal-body">
          <div className="field">
            <label>Datum</label>
            <input
              type="date"
              value={datum}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setDatum(e.target.value)}
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div className="field">
              <label>Von</label>
              <input type="time" value={von} onChange={(e) => setVon(e.target.value)} required />
            </div>
            <div className="field">
              <label>Bis</label>
              <input type="time" value={bis} onChange={(e) => setBis(e.target.value)} required />
            </div>
            <div className="field">
              <label>Dauer (Min.)</label>
              <select value={dauer} onChange={(e) => setDauer(Number(e.target.value))}>
                {[15, 30, 45, 60, 90, 120].map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div className="field">
            <label>Notiz (optional, wird Patienten angezeigt)</label>
            <input
              type="text"
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              placeholder="z. B. Routineuntersuchung, Vorsorge"
              maxLength={500}
            />
          </div>

          <div style={{ background: '#F4FAFA', padding: 12, borderRadius: 8, fontSize: 13 }}>
            <strong>Vorschau:</strong> {preview.length === 0 ? '—' : `${preview.length} Slots`}
            {preview.length > 0 && (
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {preview.slice(0, 30).map((t) => (
                  <span key={t} style={{ background: '#fff', padding: '2px 8px', borderRadius: 4, border: '1px solid #DDE9E8', fontSize: 12 }}>
                    {t}
                  </span>
                ))}
                {preview.length > 30 && <span className="muted xs">+ {preview.length - 30} weitere</span>}
              </div>
            )}
          </div>

          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="btn-secondary" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn-primary" disabled={submitting || preview.length === 0}>
              {submitting ? 'Sende…' : `${preview.length} Slots anlegen`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function generateTimeSlots(von: string, bis: string, dauerMin: number): string[] {
  const [vh, vm] = von.split(':').map(Number);
  const [bh, bm] = bis.split(':').map(Number);
  const start = vh * 60 + vm;
  const end = bh * 60 + bm;
  if (end <= start || dauerMin <= 0) return [];
  const slots: string[] = [];
  for (let t = start; t + dauerMin <= end; t += dauerMin) {
    const h = Math.floor(t / 60).toString().padStart(2, '0');
    const m = (t % 60).toString().padStart(2, '0');
    slots.push(`${h}:${m}`);
  }
  return slots;
}

function TerminCard({ termin, onStorno }: { termin: TerminVorschlag; onStorno: (id: string) => void }) {
  const past = Date.parse(`${termin.termin_datum}T${termin.termin_uhrzeit}:00`) < Date.now();
  return (
    <div className={`termin-card status-${termin.status}`}>
      <div className="termin-head">
        <div className="termin-date">
          <div className="d-day">{Number(termin.termin_datum.split('-')[2])}</div>
          <div className="d-month">{monthShort(termin.termin_datum)}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div className="termin-patient">{termin.patient_name ?? '[gelöscht]'}</div>
          <div className="termin-time">{formatDateLong(termin.termin_datum)} · {termin.termin_uhrzeit} Uhr</div>
          <span className={`status status-${termin.status}`}>{statusLabel(termin.status)}</span>
          {past && termin.status === 'offen' && <span className="muted xs"> · in der Vergangenheit</span>}
        </div>
      </div>
      {termin.notiz && (
        <div className="termin-notiz">
          <strong>Ihre Notiz:</strong> {termin.notiz}
        </div>
      )}
      {termin.patient_kommentar && (
        <div className={`termin-notiz ${termin.status === 'abgelehnt' ? 'rejected' : ''}`}>
          <strong>Patient:</strong> {termin.patient_kommentar}
        </div>
      )}
      <div className="termin-meta muted xs">
        Vorgeschlagen {new Date(termin.vorgeschlagen_am).toLocaleString('de-DE')}
        {termin.beantwortet_am && <> · Antwort {new Date(termin.beantwortet_am).toLocaleString('de-DE')}</>}
      </div>
      {termin.status === 'offen' && (
        <div className="row" style={{ marginTop: 8 }}>
          <button className="btn-secondary" onClick={() => onStorno(termin.id)}>Vorschlag zurückziehen</button>
        </div>
      )}
    </div>
  );
}

function statusLabel(s: TerminVorschlag['status']): string {
  return {
    offen: 'Wartet auf Antwort',
    angenommen: '✅ Angenommen',
    abgelehnt: '❌ Abgelehnt',
    storniert: 'Zurückgezogen',
  }[s];
}

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [patienten, setPatienten] = useState<PatientPublic[]>([]);
  const [patientId, setPatientId] = useState('');
  const [datum, setDatum] = useState('');
  const [uhrzeit, setUhrzeit] = useState('09:00');
  const [notiz, setNotiz] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    listPatienten().then((r) => setPatienten(r.patienten)).catch(() => {});
  }, []);

  const valid = patientId && /^\d{4}-\d{2}-\d{2}$/.test(datum) && /^\d{2}:\d{2}$/.test(uhrzeit);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      await vorschlagTermin(patientId, {
        termin_datum: datum,
        termin_uhrzeit: uhrzeit,
        notiz: notiz.trim() || undefined,
      });
      onCreated();
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Standard-Datum: morgen
  useEffect(() => {
    if (!datum) {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      setDatum(d.toISOString().slice(0, 10));
    }
  }, [datum]);

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-card" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Termin vorschlagen</h2>
          <button className="btn-icon" onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit} className="modal-body">
          <div className="field">
            <label>Patient/in</label>
            <select
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
              required
              style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 }}
            >
              <option value="">— Patient auswählen —</option>
              {patienten.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.vorname} {p.nachname} (geb. {p.geburtsdatum}){p.has_push ? ' · 📱' : ''}
                </option>
              ))}
            </select>
            {patientId && !patienten.find((p) => p.id === patientId)?.has_push && (
              <p className="muted small" style={{ marginTop: 4 }}>
                ⚠ Dieser Patient hat keine Push-Notifications aktiviert. Er sieht den Vorschlag erst beim nächsten App-Öffnen.
              </p>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="field">
              <label>Datum</label>
              <input
                type="date"
                value={datum}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setDatum(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>Uhrzeit</label>
              <input
                type="time"
                value={uhrzeit}
                onChange={(e) => setUhrzeit(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="field">
            <label>Notiz (optional)</label>
            <textarea
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="z. B. Grund für den Termin, Vorbereitung, etc."
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="btn-secondary" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn-primary" disabled={!valid || submitting}>
              {submitting ? 'Sende…' : 'Vorschlag senden'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function formatDateLong(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function monthShort(iso: string): string {
  const m = Number(iso.split('-')[1]);
  return ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'][m - 1] ?? '';
}
